/**
 * ErrorBoundary — Filet de sécurité global pour toute erreur React non gérée.
 *
 * Design conforme au Design System WYVIO :
 *   - fond blanc dominant
 *   - card noire accent avec halo lime
 *   - CTAs Réessayer (noir plein) + Retour à l'accueil (ghost outline)
 *
 * Aucune information technique n'est exposée à l'utilisateur final :
 *   - pas de stacktrace
 *   - pas de nom de composant
 *   - pas de message d'exception
 *   - journalisation console uniquement en dev
 */
import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (process.env.NODE_ENV === "development") {
      // Journalisation locale uniquement en développement — silence total en prod.
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, info);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="global-error-boundary">
        <div
          className="relative max-w-lg w-full rounded-3xl text-white p-10 lg:p-12 shadow-2xl shadow-black/15 overflow-hidden"
          style={{ background: "#111111" }}
        >
          <div
            aria-hidden
            className="absolute -top-16 -right-16 h-56 w-56 rounded-full blur-3xl pointer-events-none"
            style={{ background: "rgba(200,255,47,0.22)" }}
          />
          <div className="relative">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#C8FF2F" }}>
              WYVIO
            </p>
            <h1 className="mt-4 text-[28px] lg:text-[32px] font-semibold tracking-[-0.02em] leading-[1.15]">
              Un problème est survenu.
            </h1>
            <p className="mt-4 text-[14.5px] leading-[1.65] text-white/70">
              Nous n&apos;avons pas pu afficher cette page. Vous pouvez réessayer ou
              revenir à l&apos;accueil.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={this.handleRetry}
                data-testid="error-retry"
                className="h-12 inline-flex items-center rounded-full text-[14px] font-semibold px-6 transition-all hover:brightness-95"
                style={{ background: "#C8FF2F", color: "#111111" }}
              >
                Réessayer
              </button>
              <button
                type="button"
                onClick={this.handleHome}
                data-testid="error-home"
                className="h-12 inline-flex items-center rounded-full border border-white/15 text-white text-[14px] font-medium px-6 hover:bg-white/5 transition-colors"
              >
                Retour à l&apos;accueil
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
